import i18next from 'i18next'
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
  useMemo
} from 'react'
import { Col, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'

import {
  StyledLoadingOverlay,
  StyledRow,
  StyledRowText,
  StyledRowTitle,
  StyledUserCard,
  StyledUserCardContent,
  StyledUserCardContentItem,
  StyledUserCardHeader,
  StyledUserCardHeaderAction,
  StyledUserCardHeaderInfo,
  StyledUserCardProfile,
  StyledUserCardProfileAvatar,
  StyledUserName
} from './AccountDetailsUserCard.styles'
import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinCard } from '../../../components/ClinCard'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinText } from '../../../components/ClinText'
import { ContactTitles, UserRole } from '../../../constants/user'
import { IRole } from '../../../types'
import { IContact } from '../../../types/IContact'
import { getSquidExObjectForKeyAndValue } from '../../../utils/getSquidExObjectForKeyAndValue'
import { ClinGroup } from '../../../components/ClinGroup'
import { AccountDetailsEditUserForm } from './AccountDetailsEditUserForm'
import {
  AuthError,
  cancelGetContactDetails,
  getContactDetails
} from '../../../services/ApiService'
import { AxiosError } from 'axios'
import { createAnnounceEvent } from '../../../events/AnnounceEvent'
import { AnnounceMode } from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import { AppContext } from '../../../context/app'
import {
  InstituteContactDto,
  UpdateContactDto,
  LovDto
} from '../../../types/swaggerTypes'
import { StyledClinSecondaryButton2 } from './AccountDetails.styles'
import { formatValueLabel } from '../../../utils/formatValueLabel'
import {
  displayAccountValue,
  normalizeAccountValue
} from './AccountDetails.helpers'

export interface IAccountDetailsUserCardStyles {
  isPending?: boolean
}

export interface IAccountDetailsUserCardProps
  extends IAccountDetailsUserCardStyles {
  userKey: string
  isUser: boolean
  contact: IContact
  onEditUser?: (userKey: string) => void
  onRemoveUser?: (user: IContact, isInvite: boolean) => void
  onRequestInvite?: (user: IContact) => void
  editingKey: string | null
  handleUserFormSubmission: (
    data: UpdateContactDto,
    contact: InstituteContactDto
  ) => void
  handleUserFormCancel: () => void
  isUserSubmitting: boolean
  specialisms: LovDto[]
  jobTitles: LovDto[]
  isSpecialismsLoading: boolean
  isJobTitlesLoading: boolean
}

export const AccountDetailsUserCard: FunctionComponent<
  IAccountDetailsUserCardProps
> = ({
  userKey,
  isUser,
  isPending,
  contact,
  editingKey,
  onEditUser,
  onRemoveUser,
  onRequestInvite,
  handleUserFormSubmission,
  handleUserFormCancel,
  isUserSubmitting,
  specialisms,
  jobTitles,
  isSpecialismsLoading,
  isJobTitlesLoading
}) => {
  const { t } = useTranslation()
  const { dispatch, institute } = useContext(AppContext)
  const [contactDetails, setContactDetails] = useState<
    InstituteContactDto | undefined
  >()
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const contactTitlesOptions = Object.values(ContactTitles)
  const contactTitlesArray = contactTitlesOptions.map((item) => item.valueOf())
  const name = normalizeAccountValue(contact.contactName).split(' ')
  const initials = name
    .filter((item) => !contactTitlesArray.includes(item))
    .map((item) => item.charAt(0))
    .join('')

  const role = getSquidExObjectForKeyAndValue(
    'key',
    contact.clientRelationships[0]?.roleType as UserRole,
    i18next.t('user_roles:roles', {
      returnObjects: true
    })
  ) as IRole

  const isEditing = userKey === editingKey
  const isLicensed = ['pharmacist', 'physician'].includes(
    normalizeAccountValue(contact.classification).toLowerCase()
  )

  useEffect(() => {
    setIsLoading(true)
    contact.contactId &&
      getContactDetails(contact.contactId.toString())
        .then((response) => {
          setContactDetails(response.data)
          setIsLoading(false)
        })
        .catch((error: AxiosError) => {
          const { code, message } = error
          // If request is cancelled continue
          if (error.message === AuthError.RequestCancelled) {
            return
          }
          // If we have a full error show it
          if (error.response) {
            const { title, detail } = error.response.data
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error retrieving this contact. ${
                  title ? title : ''
                } ${message ? message : ''}`
              )
            )
            console.warn(title, detail)
          } else {
            dispatch(
              createAnnounceEvent(
                AnnounceMode.Error,
                `There was an error retrieving this contact. ${message} ${
                  code ? code : ''
                }`
              )
            )
          }
          setIsLoading(false)
        })
    return cancelGetContactDetails()
  }, [contact, dispatch, institute])

  const displayLicense = useMemo(() => {
    const licenseFromDetails = normalizeAccountValue(
      contactDetails?.licenseNumber
    )
    const licenseFromContact = normalizeAccountValue(contact?.licenseNumber)
    const effective = licenseFromDetails || licenseFromContact

    return isLicensed && effective ? effective : 'None'
  }, [isLicensed, contactDetails?.licenseNumber, contact?.licenseNumber])

  return (
    <ClinCard
      removePadding={true}
      style={{
        border: 'none',
        marginBottom: ClinTheme.space[2]
      }}
    >
      <StyledUserCard isPending={isPending}>
        {contact.isLoading && (
          <StyledLoadingOverlay>
            <ClinSpinner size={ClinTheme.space[7]} />
          </StyledLoadingOverlay>
        )}
        <StyledUserCardHeader>
          <StyledUserCardProfile>
            <StyledUserCardProfileAvatar className={'avatar'}>
              {initials}
            </StyledUserCardProfileAvatar>
          </StyledUserCardProfile>
          <StyledUserCardHeaderInfo>
            <div>
              <ClinGroup>
                <ClinText
                  fontWeight={ClinTheme.fontWeights.medium}
                  lineHeight="18px"
                >
                  {displayAccountValue(contact.contactName)}
                </ClinText>
                {normalizeAccountValue(contact.preferredName) && (
                  <StyledUserName>
                    {normalizeAccountValue(contact.preferredName)}
                  </StyledUserName>
                )}
              </ClinGroup>
              <ClinText wordBreak="break-word" lineHeight="20px">
                {displayAccountValue(contact?.contactEmail)}
              </ClinText>
            </div>

            <StyledUserCardHeaderAction>
              {isPending && onRequestInvite && (
                <StyledClinSecondaryButton2
                  style={{
                    marginRight: ClinTheme.space[3]
                  }}
                  variant="secondary"
                  onClick={() => onRequestInvite(contact)}
                >
                  {t('common:buttons.resend')}
                </StyledClinSecondaryButton2>
              )}
              {!isPending && onEditUser && (
                <StyledClinSecondaryButton2
                  style={{
                    marginRight: ClinTheme.space[3]
                  }}
                  disabled={isEditing}
                  variant="secondary"
                  onClick={() => onEditUser(userKey)}
                >
                  {t('common:buttons.edit')}
                </StyledClinSecondaryButton2>
              )}
              <ClinButton
                disabled={isUser}
                variant="linkButton"
                fontSize={ClinTheme.fontSizes[2]}
                justifyContent="center"
                onClick={() =>
                  onRemoveUser && onRemoveUser(contact, !!isPending)
                }
              >
                {isPending
                  ? t('common:buttons.cancel')
                  : t('common:buttons.delete')}
              </ClinButton>
            </StyledUserCardHeaderAction>
          </StyledUserCardHeaderInfo>
        </StyledUserCardHeader>

        {!isEditing ? (
          <StyledUserCardContent>
            <StyledUserCardContentItem paddingRight borderRight>
              <ClinText
                fontWeight={ClinTheme.fontWeights.bold}
                lineHeight="20px"
                marginBottom={ClinTheme.space[3]}
              >
                {t('account_details:job_titles')}
              </ClinText>
              <StyledRow>
                <Row>
                  <Col>
                    <StyledRowTitle>
                      {t('institute_details:job_type')}
                    </StyledRowTitle>
                    <StyledRowText>
                      {displayAccountValue(
                        formatValueLabel(
                          normalizeAccountValue(contact.classification),
                          jobTitles
                        )
                      )}
                    </StyledRowText>
                  </Col>
                </Row>
              </StyledRow>
              <StyledRow>
                <Row>
                  <Col>
                    <StyledRowTitle>
                      {t('institute_details:specialism')}
                    </StyledRowTitle>
                    <StyledRowText>
                      {displayAccountValue(
                        formatValueLabel(
                          normalizeAccountValue(contact.specialism),
                          specialisms
                        )
                      )}
                    </StyledRowText>
                  </Col>
                </Row>
              </StyledRow>
              <StyledRow>
                <Row>
                  <Col>
                    <StyledRowTitle>
                      {t('common:labels.license_number')}:
                    </StyledRowTitle>
                    <StyledRowText>{displayLicense}</StyledRowText>
                  </Col>
                </Row>
              </StyledRow>
            </StyledUserCardContentItem>
            <StyledUserCardContentItem paddingLeft>
              <ClinText
                fontWeight={ClinTheme.fontWeights.bold}
                lineHeight="20px"
                marginBottom={ClinTheme.space[3]}
              >
                {t('account_details:security')}
              </ClinText>
              <StyledRow>
                <Row>
                  <Col>
                    <StyledRowTitle>
                      {t('institute_details:permissions')}
                    </StyledRowTitle>
                    <StyledRowText>
                      {displayAccountValue(
                        role?.contact_type
                          ? role.contact_description
                          : undefined
                      )}
                    </StyledRowText>
                  </Col>
                </Row>
              </StyledRow>
              <StyledRow>
                <Row>
                  <Col>
                    <StyledRowTitle>
                      {t('institute_details:phone_number')}
                    </StyledRowTitle>
                    {(contact.phones ?? []).map((item, index) => (
                      <StyledRowText key={`contact-number-${index}`}>
                        {displayAccountValue(item.phoneNumber)}
                      </StyledRowText>
                    ))}
                    {!contact.phones?.length && (
                      <StyledRowText>--</StyledRowText>
                    )}
                  </Col>
                </Row>
              </StyledRow>
            </StyledUserCardContentItem>
          </StyledUserCardContent>
        ) : (
          <AccountDetailsEditUserForm
            isLoading={isLoading || isSpecialismsLoading || isJobTitlesLoading}
            contact={contactDetails}
            specialisms={specialisms}
            jobTitles={jobTitles}
            handleUserFormSubmission={handleUserFormSubmission}
            handleUserFormCancel={handleUserFormCancel}
            isUserSubmitting={isUserSubmitting}
          />
        )}
      </StyledUserCard>
    </ClinCard>
  )
}
